import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  increment,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebase";

export const BUG_TYPES = [
  "Bug",
  "UI Bug",
  "Functional Bug",
  "Performance Bug",
  "Security Bug",
  "Enhancement",
];

export const BUG_PRIORITIES = ["Low", "Medium", "High", "Critical"];
export const BUG_SEVERITIES = ["Minor", "Major", "Critical", "Blocker"];
export const BUG_ENVIRONMENTS = ["Dev", "QA", "UAT", "Production"];
export const BUG_STATUSES = ["Open", "In Progress", "Retest", "Reopened", "Resolved", "Closed", "Deferred"];
export const BUG_RESOLUTIONS = ["", "Fixed", "Duplicate", "Cannot Reproduce", "Won't Fix", "Deferred"];

const bugsCollection = collection(db, "bugs");

function toDateValue(value) {
  if (!value) return null;
  return typeof value.toDate === "function" ? value.toDate() : new Date(value);
}

function normalizeBugDoc(snapshot) {
  const data = snapshot.data();
  return {
    id: snapshot.id,
    ...data,
    createdAtDate: toDateValue(data.createdAt),
    updatedAtDate: toDateValue(data.updatedAt),
  };
}

export function formatBugDate(value) {
  const date = toDateValue(value);
  if (!date || Number.isNaN(date.getTime())) return "-";

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getUserLabel(user) {
  return user?.displayName || user?.email || "QA Coder User";
}

async function getNextBugId() {
  const counterRef = doc(db, "counters", "bugs");

  return runTransaction(db, async (transaction) => {
    const counterSnapshot = await transaction.get(counterRef);
    const currentValue = counterSnapshot.exists() ? counterSnapshot.data().nextNumber || 1 : 1;
    const bugId = `BUG-${String(currentValue).padStart(3, "0")}`;

    transaction.set(counterRef, { nextNumber: currentValue + 1, updatedAt: serverTimestamp() }, { merge: true });
    return bugId;
  });
}

function normalizeAttachments(files = []) {
  return Array.from(files).map((file) => ({
    name: file.name,
    type: file.type || "unknown",
    size: file.size,
    category: file.type?.startsWith("image/")
      ? "screenshot"
      : file.type?.startsWith("video/")
        ? "video"
        : file.name.endsWith(".log")
          ? "logs"
          : "document",
  }));
}

async function addBugActivity(bugDocId, entry) {
  return addDoc(collection(db, "bugs", bugDocId, "activity"), {
    ...entry,
    createdAt: serverTimestamp(),
  });
}

export async function createBug(payload, files, user) {
  const bugId = await getNextBugId();
  const reporterName = getUserLabel(user);
  const attachments = normalizeAttachments(files);

  const bugRef = await addDoc(bugsCollection, {
    ...payload,
    bugId,
    status: payload.status || "Open",
    resolution: payload.resolution || "",
    reportedById: user.uid,
    reportedByName: reporterName,
    reportedByEmail: user.email || "",
    commentsCount: 0,
    attachments,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  await addBugActivity(bugRef.id, {
    type: "created",
    message: `${reporterName} created ${bugId}.`,
    actorId: user.uid,
    actorName: reporterName,
  });

  if (payload.assignedToId) {
    await addBugActivity(bugRef.id, {
      type: "assigned",
      message: `${bugId} assigned to ${payload.assignedToName || "a team member"}.`,
      actorId: user.uid,
      actorName: reporterName,
    });
  }

  return { id: bugRef.id, bugId };
}

export async function getBugsForUser({ userId, role }) {
  const canSeeAll = ["admin", "trainer"].includes(role);
  const bugsQuery = canSeeAll
    ? bugsCollection
    : query(bugsCollection, where("reportedById", "==", userId));
  const snapshot = await getDocs(bugsQuery);
  return snapshot.docs
    .map(normalizeBugDoc)
    .sort((first, second) => (second.createdAtDate?.getTime() || 0) - (first.createdAtDate?.getTime() || 0));
}

export async function getBugById(id) {
  const snapshot = await getDoc(doc(db, "bugs", id));
  return snapshot.exists() ? normalizeBugDoc(snapshot) : null;
}

export async function updateBug(id, updates, user) {
  const bugRef = doc(db, "bugs", id);
  const beforeSnapshot = await getDoc(bugRef);
  const before = beforeSnapshot.exists() ? beforeSnapshot.data() : {};

  await updateDoc(bugRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });

  const actorName = getUserLabel(user);
  const activityMessages = [];

  if (updates.status && updates.status !== before.status) {
    activityMessages.push(`Status changed from ${before.status || "Open"} to ${updates.status}.`);
  }

  if (updates.assignedToId !== undefined && updates.assignedToId !== before.assignedToId) {
    activityMessages.push(`Assignee changed to ${updates.assignedToName || "Unassigned"}.`);
  }

  if (updates.resolution !== undefined && updates.resolution !== before.resolution) {
    activityMessages.push(`Resolution changed to ${updates.resolution || "None"}.`);
  }

  for (const message of activityMessages) {
    await addBugActivity(id, {
      type: "updated",
      message,
      actorId: user.uid,
      actorName,
    });
  }
}

export async function addBugComment(id, comment, user) {
  const actorName = getUserLabel(user);
  await addDoc(collection(db, "bugs", id, "comments"), {
    comment,
    actorId: user.uid,
    actorName,
    actorEmail: user.email || "",
    createdAt: serverTimestamp(),
  });

  await updateDoc(doc(db, "bugs", id), {
    commentsCount: increment(1),
    updatedAt: serverTimestamp(),
  });

  await addBugActivity(id, {
    type: "comment",
    message: `${actorName} added a comment.`,
    actorId: user.uid,
    actorName,
  });
}

export async function getBugComments(id) {
  const commentsQuery = query(collection(db, "bugs", id, "comments"), orderBy("createdAt", "asc"));
  const snapshot = await getDocs(commentsQuery);
  return snapshot.docs.map((commentDoc) => ({ id: commentDoc.id, ...commentDoc.data() }));
}

export async function getBugActivity(id) {
  const activityQuery = query(collection(db, "bugs", id, "activity"), orderBy("createdAt", "asc"));
  const snapshot = await getDocs(activityQuery);
  return snapshot.docs.map((activityDoc) => ({ id: activityDoc.id, ...activityDoc.data() }));
}
